import React, { useEffect, useState } from "react"
import { Box, Button, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { Layout } from "src/components/layout"
import { PROVINCES, SIDE_MENU, SNACKBAR_TYPES, USER_TYPE } from "src/modules/const"
import { useNavigate, useParams } from "react-router"
import { useAuth } from "src/providers/LoginProvider"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "../../states/showToast"
import { useRecoilState } from "recoil"
import { validateFields, validateField } from "src/modules/validation"
import { AddressService, CompanyService } from "src/openapi"
import InputTextField from "src/components/atom/InputTextField"
import SelectField from "src/components/atom/SelectField"
import BreadCrumb from "src/components/atom/BreadCrumb"
import * as yup from "yup"
import "./index.scss"
import { Top } from "../top"

export const CompanyEdit = () => {
  const { t } = useTranslation("translation", { keyPrefix: "company" })

  const { companyId } = useParams()

  const [companyName, setCompanyName] = useState<string>("")
  const [ceoName, setCEOName] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [province, setProvince] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [street, setStreet] = useState<string>("")
  const [building, setBuilding] = useState<string>("")
  const [postCode, setPostCode] = useState<string>("")
  const [mailAddress, setMailAddress] = useState<string>("")

  const [companyValidationMessage, setCompanyValidationMessage] = useState<string>("")
  const [ceoValidationMessage, setCeoValidationMessage] = useState<string>("")
  const [phoneValidationMessage, setPhoneValidationMessage] = useState<string>("")
  // const [provinceValidationMessage, setProvinceValidationMessage] = useState<string>("")
  const [cityValidationMessage, setCityValidationMessage] = useState<string>("")
  const [streetValidationMessage, setStreetValidationMessage] = useState<string>("")
  const [buildingValidationMessage, setBuildingValidationMessage] = useState<string>("")
  const [codeValidationMessage, setCodeValidationMessage] = useState<string>("")
  const [mailValidationMessage, setMailValidationMessage] = useState<string>("")

  const navigate = useNavigate()

  const { user } = useAuth()

  const [, setShowProgress] = useRecoilState(ShowProgress)
  const [, setShowToast] = useRecoilState(ShowToast)

  const handleGetCompany = () => {
    if (user) {
      setShowProgress(true)
      CompanyService.getCompany(user.token, Number(companyId))
        .then((res) => {
          setShowProgress(false)
          setCompanyName(res.companyName!)
          setCEOName(res.ceo!)
          setPhoneNumber(res.phoneNumber!)
          setProvince(res.province!)
          setCity(res.city!)
          setStreet(res.address!)
          setBuilding(res.buildingNo!)
          setPostCode(res.postCode!)
          setMailAddress(res.email!)
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({
            show: true,
            text: t("failed_get_company_info"),
            type: SNACKBAR_TYPES.ERROR,
          })
        })
    }
  }

  const handleUpdateCompany = async () => {
    if (user == null) {
      return
    }
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    CompanyService.updateCompany(
      Number(companyId),
      {
        companyName: companyName,
        ceo: ceoName,
        email: mailAddress,
        postCode: postCode,
        province: province,
        city: city,
        address: street,
        buildingNo: building,
        phoneNumber: phoneNumber,
        comment: "",
      },
      user.token
    )
      .then(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("success_update_company"),
          type: SNACKBAR_TYPES.SUCCESS,
        })
        navigate("/company")
      })
      .catch(() => {
        setShowToast({
          show: true,
          text: t("failed_update_company"),
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  const validate = async () => {
    let validation = true
    try {
      await validateFields(
        yup.object().shape({
          companyName: yup.string().required(t("please_input_company_name")),
        }),
        ["companyName"],
        [companyName]
      )
      setCompanyValidationMessage("")
    } catch (err: any) {
      setCompanyValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateFields(
        yup.object().shape({
          ceoName: yup.string().required(t("please_input_ceo_name")),
        }),
        ["ceoName"],
        [ceoName]
      )
      setCeoValidationMessage("")
    } catch (err: any) {
      setCeoValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateFields(
        yup.object().shape({
          postCode: yup.string().required(t("please_input_post_code")),
        }),
        ["postCode"],
        [postCode]
      )
      setCodeValidationMessage("")
    } catch (err: any) {
      setCodeValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateFields(
        yup.object().shape({
          city: yup.string().required(t("please_input_city")),
        }),
        ["city"],
        [city]
      )
      setCityValidationMessage("")
    } catch (err: any) {
      setCityValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateFields(
        yup.object().shape({
          street: yup.string().required(t("please_input_street")),
        }),
        ["street"],
        [street]
      )
      setStreetValidationMessage("")
    } catch (err: any) {
      setStreetValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateFields(
        yup.object().shape({
          building: yup.string().required(t("please_input_building")),
        }),
        ["building"],
        [building]
      )
      setBuildingValidationMessage("")
    } catch (err: any) {
      setBuildingValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          phoneNumber: yup
            .string()
            .required(t("please_input_phone_number"))
            .matches(
              /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
              t("please_input_correct_phone_number")
            ),
        }),
        "phoneNumber",
        phoneNumber
      )
      setPhoneValidationMessage("")
    } catch (err: any) {
      setPhoneValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          mailAddress: yup
            .string()
            .required(t("please_input_email"))
            .email(t("please_input_correct_email")),
        }),
        "mailAddress",
        mailAddress
      )
      setMailValidationMessage("")
    } catch (err: any) {
      setMailValidationMessage(err.message)
      validation = validation && false
    }

    return validation
  }

  const handlePostCodeInput = () => {
    if (!user) {
      return
    }
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode, user.token)
      .then((res) => {
        if (res.prefecture) {
          setProvince(res.prefecture as string)
        }

        if (res.city) {
          let city = res.city!
          if (res.townCandidates && res.townCandidates.length >= 1) {
            city = `${city}${res.townCandidates[0]}`
          }
          setCity(city)
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (user == null) {
      return
    }
    if (user.type != USER_TYPE.GROUPADMIN) {
      return
    }

    handleGetCompany()
  }, [user])

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.COMPANY_LIST}>
      <BreadCrumb
        paths={[
          {
            name: t("title"),
            link: "",
          },
        ]}
      />
      <Typography className="company-description">{t("update_description")}</Typography>
      <Box className="company-content-container">
        <Box className="company-item-container">
          <Box className="company-item-title">{t("company_name")}</Box>
          <Box className="company-item-content-container">
            <InputTextField
              text={companyName}
              handleText={setCompanyName}
              inputStyle={{ width: "300px", height: "54px" }}
              validationMessage={companyValidationMessage}
              placeholder={t("company_name")}
            />
          </Box>
        </Box>
        <Box className="company-item-container">
          <Box className="company-item-title">{t("ceo_name")}</Box>
          <Box className="company-item-content-container">
            <InputTextField
              text={ceoName}
              handleText={setCEOName}
              inputStyle={{ width: "300px", height: "54px" }}
              validationMessage={ceoValidationMessage}
              placeholder={t("ceo_name")}
            />
          </Box>
        </Box>
        <Box className="company-item-container">
          <Box className="company-item-title">{t("post_code")}</Box>
          <Box className="company-item-content-container">
            <InputTextField
              text={postCode}
              handleText={setPostCode}
              handleFocusOut={handlePostCodeInput}
              inputStyle={{ width: "300px", height: "54px" }}
              validationMessage={codeValidationMessage}
              placeholder="例）1234567"
            />
          </Box>
        </Box>
        <Box className="company-item-container">
          <Box className="company-item-title">{t("address")}</Box>
          <Box className="company-item-content-container">
            <SelectField
              selected={province}
              handleSelected={setProvince}
              datas={PROVINCES}
              inputStyle={{ width: "120px", height: "54px" }}
              sx={{ mb: "10px" }}
            />
            <InputTextField
              text={city}
              handleText={setCity}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              validationMessage={cityValidationMessage}
              placeholder={t("city")}
            />
            <InputTextField
              text={street}
              handleText={setStreet}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              validationMessage={streetValidationMessage}
              placeholder={t("street")}
            />
            <InputTextField
              text={building}
              handleText={setBuilding}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              validationMessage={buildingValidationMessage}
              placeholder={t("building")}
            />
          </Box>
        </Box>
        <Box className="company-item-container">
          <Box className="company-item-title">{t("phone_number")}</Box>
          <Box className="company-item-content-container">
            <InputTextField
              text={phoneNumber}
              handleText={setPhoneNumber}
              inputStyle={{ width: "300px", height: "54px" }}
              validationMessage={phoneValidationMessage}
              placeholder="例）09012345678"
            />
          </Box>
        </Box>
        <Box className="company-item-container">
          <Box className="company-item-title">{t("mail_address")}</Box>
          <Box className="company-item-content-container">
            <InputTextField
              text={mailAddress}
              handleText={setMailAddress}
              inputStyle={{ width: "300px", height: "54px" }}
              validationMessage={mailValidationMessage}
              placeholder="例）example@mail.com"
            />{" "}
          </Box>
        </Box>
      </Box>
      <Button className="company-update-save-button" onClick={() => handleUpdateCompany()}>
        {t("save")}
      </Button>
      <Button className="company-update-back-button" onClick={() => navigate("/company")}>
        {t("back")}
      </Button>
    </Layout>
  )
}
